import { useFragment, graphql } from 'react-relay';
import { filterNulls } from 'dibs-ts-utils/exports/filterNulls';
import { ArrayElement } from 'dibs-ts-utils/exports/ArrayElement';

import {
    useCheckoutIsLoggedIntoPayPal_buyer$key,
    useCheckoutIsLoggedIntoPayPal_buyer$data,
} from './__generated__/useCheckoutIsLoggedIntoPayPal_buyer.graphql';
import { usePayPalPaymentMethod } from './usePayPalPaymentMethod';

type useCheckoutIsLoggedIntoPayPalArg = {
    buyer: useCheckoutIsLoggedIntoPayPal_buyer$key;
    shippingAddressId: string;
    payPalToken?: string;
};

type WalletPaymentMethod = NonNullable<
    ArrayElement<NonNullable<useCheckoutIsLoggedIntoPayPal_buyer$data['wallet']>['paymentMethods']>
>;

type PaymentMethod = WalletPaymentMethod['paymentMethod'];
type PayPalAccount = Extract<PaymentMethod, { __typename: 'PayPalAccount' }>;

function isPayPalMethod(paymentMethod: PaymentMethod): paymentMethod is PayPalAccount {
    return !!paymentMethod && paymentMethod.__typename === 'PayPalAccount';
}

export function useCheckoutIsLoggedIntoPayPal({
    buyer: buyerRef,
    shippingAddressId,
}: useCheckoutIsLoggedIntoPayPalArg): boolean {
    const buyer = useFragment(
        graphql`
            fragment useCheckoutIsLoggedIntoPayPal_buyer on User {
                ...usePayPalPaymentMethod_buyer
                wallet {
                    paymentMethods {
                        shippingAddresses {
                            serviceId
                        }
                        paymentMethod {
                            __typename
                            ... on PayPalAccount {
                                paymentMethodType
                                token
                            }
                        }
                    }
                }
            }
        `,
        buyerRef
    );

    const { token } = usePayPalPaymentMethod({ buyer });

    const paymentMethods = buyer?.wallet?.paymentMethods;

    if (!token) {
        return false;
    }

    const payPalWalletPaymentMethod = (paymentMethods || [])
        .filter(filterNulls)
        .find(
            walletPaymentMethod =>
                isPayPalMethod(walletPaymentMethod.paymentMethod) &&
                walletPaymentMethod.paymentMethod?.token === token
        );

    if (!payPalWalletPaymentMethod) {
        return false;
    }

    const isValidForShippingAddress = !!payPalWalletPaymentMethod?.shippingAddresses
        ?.filter(filterNulls)
        .find(address => address.serviceId === shippingAddressId);

    return isValidForShippingAddress;
}
